/**
 * Tracks the External Players settings tab: the page view (split by premium
 * status, since the tab pitches premium to free members), and the three actions
 * a member can take there — downloading a playlist file, copying a stream URL,
 * and regenerating their stream key.
 *
 * Stateless by design: every method is a pass-through to mixpanelService, so
 * unlike its sibling trackers there is no in-memory correlation state and thus
 * no `.instance` guard — `export default new ExternalPlayerTracker()` already
 * gives every importer the one shared instance.
 */

import mixpanelService from '../mixpanel-service'
import { EVENTS, PROPS } from './analytics-spec'

class ExternalPlayerTracker {
    /**
     * Track a view of the External Players settings tab. `is_premium` is
     * coerced to a real boolean so the free/premium split is never skewed by
     * an undefined slipping into the property.
     */
    onPageViewed(isPremium) {
        mixpanelService.track(EVENTS.EXTERNAL_PLAYER_PAGE_VIEWED, {
            [PROPS.IS_PREMIUM]: Boolean(isPremium)
        })
    }

    /** Track a playlist file (.pls / .m3u) download for a channel. */
    onPlaylistDownloaded({ channel, quality, format }) {
        mixpanelService.track(EVENTS.EXTERNAL_PLAYER_PLAYLIST_DOWNLOADED, {
            [PROPS.RADIO_CHANNEL_NAME]: channel,
            [PROPS.STREAM_QUALITY]: quality,
            [PROPS.PLAYLIST_FORMAT]: format
        })
    }

    /** Track a raw stream URL copied to the clipboard — no playlist format here. */
    onUrlCopied({ channel, quality }) {
        mixpanelService.track(EVENTS.EXTERNAL_PLAYER_URL_COPIED, {
            [PROPS.RADIO_CHANNEL_NAME]: channel,
            [PROPS.STREAM_QUALITY]: quality
        })
    }

    /** Track the member regenerating their personal stream key. */
    onKeyRegenerated() {
        mixpanelService.track(EVENTS.EXTERNAL_PLAYER_KEY_REGENERATED, {})
    }
}

export default new ExternalPlayerTracker()
